/**
 * §6.3 admin catalogue builder: category spec templates, option axes → variant matrix generator (pure, from
 * contracts), per-colour bulk image upload, stock and value per SKU, hero image, "what's in the box". Saves a
 * Product; prizes are built from products on the Prizes page or in the wizard.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  generateVariantMatrix,
  PrizeCategory,
  SPEC_TEMPLATE_AXES,
  SPEC_TEMPLATES,
  VARIANT_MATRIX_MAX,
  SupportedMoneyDto,
  type OptionAxisInput,
  type ProductAdminDto,
  type UpsertProductRequest,
  type VariantInput,
} from '@windrop/contracts';
import { Button, Checkbox, Field, Input, Select, useToast } from '@windrop/ui';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ErrorState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { isApiError } from '../../lib/api/client';
import { catalogApi } from './api';
import { AssetUploader, SwatchBulkUpload } from './AssetUploader';

const CATEGORY_OPTIONS = PrizeCategory.options.map((c) => ({
  value: c,
  label: c.charAt(0) + c.slice(1).toLowerCase(),
}));
const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 32) || 'axis';
const valueId = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32) || 'value';

export interface ProductDraft {
  brandId: string;
  name: string;
  description: string;
  category: PrizeCategory;
  heroImageRef: string;
  galleryRefs: string[];
  specifications: Record<string, string>;
  inTheBox: string[];
  optionAxes: OptionAxisInput[];
  variants: VariantInput[];
  currency: SupportedMoneyDto['currency'];
  baseValueMinor: string;
  skuPrefix: string;
}

export const emptyDraft = (): ProductDraft => ({
  brandId: '',
  name: '',
  description: '',
  category: 'PHONE',
  heroImageRef: '',
  galleryRefs: [],
  specifications: Object.fromEntries(SPEC_TEMPLATES.PHONE.map((k) => [k, ''])),
  inTheBox: [],
  optionAxes: SPEC_TEMPLATE_AXES.PHONE.map((a) => ({
    axisId: a,
    name: { en: a.replace(/_/g, ' ') },
    values: [],
  })),
  variants: [],
  currency: 'XTS',
  baseValueMinor: '0',
  skuPrefix: '',
});

export const fromDto = (p: ProductAdminDto): ProductDraft => ({
  brandId: p.brandId,
  name: p.name.en ?? Object.values(p.name)[0] ?? '',
  description: p.description.en ?? Object.values(p.description)[0] ?? '',
  category: p.category,
  heroImageRef: p.heroImageRef,
  galleryRefs: p.galleryRefs,
  specifications: Object.fromEntries(
    Object.entries(p.specifications).map(([k, v]) => [k, v.en ?? Object.values(v)[0] ?? '']),
  ),
  inTheBox: p.inTheBox.map((t) => t.en ?? Object.values(t)[0] ?? ''),
  optionAxes: p.optionAxes,
  variants: p.variants,
  currency: p.variants[0]?.value.currency ?? 'XTS',
  baseValueMinor: p.variants[0]?.value.amountMinor ?? '0',
  skuPrefix: p.variants[0]?.sku.split('-')[0] ?? '',
});

export const toRequest = (d: ProductDraft): UpsertProductRequest => ({
  brandId: d.brandId,
  name: { en: d.name },
  description: { en: d.description },
  category: d.category,
  heroImageRef: d.heroImageRef,
  galleryRefs: d.galleryRefs,
  specifications: Object.fromEntries(
    Object.entries(d.specifications)
      .filter(([, v]) => v.trim())
      .map(([k, v]) => [k, { en: v }]),
  ),
  inTheBox: d.inTheBox.filter((t) => t.trim()).map((t) => ({ en: t })),
  optionAxes: d.optionAxes.filter((a) => a.values.length > 0),
  variants: d.variants,
});

/** Assigns image refs to every variant whose colour axis matches (per-colour bulk upload). */
export const assignSwatchImages = (
  variants: VariantInput[],
  colourAxisId: string,
  colourValueId: string,
  refs: string[],
): VariantInput[] =>
  variants.map((v) =>
    v.options[colourAxisId] === colourValueId
      ? { ...v, imageRefs: [...new Set([...(v.imageRefs ?? []), ...refs])] }
      : v,
  );

export const colourAxisOf = (axes: OptionAxisInput[]): OptionAxisInput | undefined =>
  axes.find((a) => /colou?r/i.test(a.axisId) || a.values.some((v) => v.swatch));

export function ProductBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const existing = useQuery({
    queryKey: ['product', id],
    queryFn: () => catalogApi.products.get(id!),
    enabled: Boolean(id),
  });
  const brands = useQuery({ queryKey: ['brands'], queryFn: () => catalogApi.brands.list() });
  const [draft, setDraft] = useState<ProductDraft | null>(id ? null : emptyDraft());
  if (id && existing.data && !draft) setDraft(fromDto(existing.data));
  const save = useMutation({
    mutationFn: (d: ProductDraft) =>
      id ? catalogApi.products.update(id, toRequest(d)) : catalogApi.products.create(toRequest(d)),
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ['products'] });
      toast({
        title: 'Product saved',
        description: `${p.variantCount} SKU${p.variantCount === 1 ? '' : 's'}`,
        tone: 'success',
      });
      navigate(`/catalog/products/${p.productId}`);
    },
  });
  if (id && existing.isError)
    return (
      <ErrorState
        title="Product not found"
        body="It may belong to another tenant."
        action={<Button href="/catalog/products">Back</Button>}
      />
    );
  if (!draft) return <p className="wd-muted">Loading…</p>;
  const error = save.error && isApiError(save.error) ? save.error : null;
  return (
    <>
      <PageHeader
        title={id ? `Edit ${draft.name || 'product'}` : 'New product'}
        breadcrumb={[
          { label: 'Sponsors & prizes', to: '/catalog' },
          { label: 'Products', to: '/catalog/products' },
          { label: id ? 'Edit' : 'New' },
        ]}
        description="Option axes become a SKU matrix; each SKU carries its own value, specs, images and stock (§6.1)."
      />
      <ProductBuilderForm
        draft={draft}
        onChange={setDraft}
        brands={(brands.data ?? []).map((b) => ({
          value: b.brandId,
          label: `${b.name}${b.approved ? '' : ' (not approved)'}`,
        }))}
      />
      {error ? (
        <div className="wdc-callout" role="alert">
          <p>{error.message}</p>
          <ul>
            {Object.entries(error.fieldErrors()).map(([k, v]) => (
              <li key={k}>
                <code>{k}</code> {v}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="wd-btn-group">
        <Button
          onClick={() => save.mutate(draft)}
          loading={save.isPending}
          disabled={!draft.name || !draft.brandId || draft.variants.length === 0}
        >
          Save product
        </Button>
        <Button variant="quiet" href="/catalog/products">
          Cancel
        </Button>
      </div>
    </>
  );
}

export function ProductBuilderForm({
  draft,
  onChange,
  brands,
}: {
  draft: ProductDraft;
  onChange: (d: ProductDraft) => void;
  brands: { value: string; label: string }[];
}) {
  const set = <K extends keyof ProductDraft>(k: K, v: ProductDraft[K]) => onChange({ ...draft, [k]: v });
  const [axisDraft, setAxisDraft] = useState('');
  const [valueDraft, setValueDraft] = useState<Record<string, string>>({});
  const combos = useMemo(
    () => draft.optionAxes.filter((a) => a.values.length).reduce((n, a) => n * a.values.length, 1),
    [draft.optionAxes],
  );
  const colourAxis = colourAxisOf(draft.optionAxes);

  const changeCategory = (c: PrizeCategory) =>
    onChange({
      ...draft,
      category: c,
      specifications: Object.fromEntries(SPEC_TEMPLATES[c].map((k) => [k, draft.specifications[k] ?? ''])),
      optionAxes:
        draft.optionAxes.length && draft.optionAxes.some((a) => a.values.length)
          ? draft.optionAxes
          : SPEC_TEMPLATE_AXES[c].map((a) => ({ axisId: a, name: { en: a.replace(/_/g, ' ') }, values: [] })),
    });

  const generate = () => {
    const axes = draft.optionAxes.filter((a) => a.values.length);
    if (!axes.length) return;
    const matrix = generateVariantMatrix({
      optionAxes: axes,
      baseValue: { amountMinor: draft.baseValueMinor || '0', currency: draft.currency },
      skuPrefix: draft.skuPrefix || draft.name.slice(0, 8) || 'SKU',
    });
    const previous = new Map(draft.variants.map((v) => [JSON.stringify(v.options), v]));
    onChange({
      ...draft,
      variants: matrix.variants.map((v) => previous.get(JSON.stringify(v.options)) ?? v),
    });
  };

  return (
    <div className="wd-stack">
      <h2>Identity</h2>
      <div className="wdc-form-grid">
        <Field label="Name" required>
          <Input
            value={draft.name}
            onChange={(e) => set('name', e.target.value)}
            data-testid="product-name"
          />
        </Field>
        <Field label="Brand" required>
          <Select
            value={draft.brandId}
            onChange={(e) => set('brandId', e.target.value)}
            options={brands}
            placeholder="Choose a brand"
            data-testid="product-brand"
          />
        </Field>
        <Field label="Category" hint="Seeds the spec template and the usual option axes.">
          <Select
            value={draft.category}
            onChange={(e) => changeCategory(e.target.value as PrizeCategory)}
            options={CATEGORY_OPTIONS}
            data-testid="product-category"
          />
        </Field>
        <Field label="Description">
          <Input value={draft.description} onChange={(e) => set('description', e.target.value)} />
        </Field>
      </div>

      <h2>Specifications ({draft.category.toLowerCase()} template)</h2>
      <div className="wdc-form-grid" data-testid="spec-template">
        {Object.keys(draft.specifications).map((k) => (
          <Field key={k} label={k}>
            <Input
              value={draft.specifications[k] ?? ''}
              onChange={(e) => set('specifications', { ...draft.specifications, [k]: e.target.value })}
            />
          </Field>
        ))}
        <Field label="Add specification key">
          <Input
            placeholder="e.g. warranty"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const k = (e.target as HTMLInputElement).value.trim();
                if (k) {
                  set('specifications', { ...draft.specifications, [k]: '' });
                  (e.target as HTMLInputElement).value = '';
                }
              }
            }}
          />
        </Field>
      </div>
      <Field label="What's in the box" hint="One item per line.">
        <textarea
          className="wd-input"
          rows={3}
          value={draft.inTheBox.join('\n')}
          onChange={(e) => set('inTheBox', e.target.value.split('\n'))}
        />
      </Field>

      <h2>Option axes</h2>
      <p className="wd-muted">
        Every valid combination becomes a SKU (max {VARIANT_MATRIX_MAX}). Current matrix:{' '}
        <strong data-testid="combo-count">{combos}</strong> combination{combos === 1 ? '' : 's'}.
      </p>
      {draft.optionAxes.map((axis, ai) => (
        <fieldset key={axis.axisId} className="wdc-fieldset wd-stack" data-testid={`axis-${axis.axisId}`}>
          <legend>
            <strong>{axis.name.en ?? axis.axisId}</strong> <code>{axis.axisId}</code>
          </legend>
          <ul className="wdc-values">
            {axis.values.map((v) => (
              <li key={v.valueId} className="wd-chip">
                {v.swatch ? (
                  <span aria-hidden className="wdc-swatch-dot" style={{ background: v.swatch }} />
                ) : null}
                {v.label.en}{' '}
                <button
                  type="button"
                  aria-label={`Remove ${v.label.en}`}
                  onClick={() =>
                    set(
                      'optionAxes',
                      draft.optionAxes.map((a, i) =>
                        i === ai ? { ...a, values: a.values.filter((x) => x.valueId !== v.valueId) } : a,
                      ),
                    )
                  }
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          <div className="wdc-actions-row">
            <Field label={`Add ${axis.name.en ?? axis.axisId} value`}>
              <Input
                value={valueDraft[axis.axisId] ?? ''}
                onChange={(e) => setValueDraft({ ...valueDraft, [axis.axisId]: e.target.value })}
                data-testid={`axis-value-${axis.axisId}`}
                placeholder={/colou?r/.test(axis.axisId) ? 'Midnight #111827' : '256GB'}
              />
            </Field>
            <Button
              variant="secondary"
              data-testid={`axis-add-${axis.axisId}`}
              onClick={() => {
                const raw = (valueDraft[axis.axisId] ?? '').trim();
                if (!raw) return;
                const m = /^(.*?)\s*(#[0-9a-fA-F]{6})?$/.exec(raw);
                const label = (m?.[1] ?? raw).trim();
                const swatch = m?.[2]?.toUpperCase();
                const id = valueId(label);
                if (axis.values.some((v) => v.valueId === id)) return;
                set(
                  'optionAxes',
                  draft.optionAxes.map((a, i) =>
                    i === ai
                      ? {
                          ...a,
                          values: [
                            ...a.values,
                            { valueId: id, label: { en: label }, ...(swatch ? { swatch } : {}) },
                          ],
                        }
                      : a,
                  ),
                );
                setValueDraft({ ...valueDraft, [axis.axisId]: '' });
              }}
            >
              Add value
            </Button>
            <Button
              variant="quiet"
              onClick={() =>
                set(
                  'optionAxes',
                  draft.optionAxes.filter((_, i) => i !== ai),
                )
              }
            >
              Remove axis
            </Button>
          </div>
        </fieldset>
      ))}
      <div className="wdc-actions-row">
        <Field label="New axis" hint="brand, model, colour, storage, trim, size…">
          <Input value={axisDraft} onChange={(e) => setAxisDraft(e.target.value)} data-testid="axis-new" />
        </Field>
        <Button
          variant="secondary"
          data-testid="axis-add"
          onClick={() => {
            const a = slug(axisDraft);
            if (!a || draft.optionAxes.some((x) => x.axisId === a)) return;
            set('optionAxes', [
              ...draft.optionAxes,
              { axisId: a, name: { en: axisDraft.trim() }, values: [] },
            ]);
            setAxisDraft('');
          }}
        >
          Add axis
        </Button>
      </div>

      <h2>Variant matrix</h2>
      <div className="wdc-form-grid">
        <Field label="SKU prefix">
          <Input
            value={draft.skuPrefix}
            onChange={(e) => set('skuPrefix', e.target.value.toUpperCase())}
            data-testid="sku-prefix"
          />
        </Field>
        <Field
          label={`Base value (minor units, ${draft.currency})`}
          hint="Applied to new SKUs; edit per SKU below."
        >
          <Input
            inputMode="numeric"
            value={draft.baseValueMinor}
            onChange={(e) => set('baseValueMinor', e.target.value.replace(/[^0-9]/g, ''))}
            data-testid="base-value"
          />
        </Field>
        <Field label="Currency">
          <Select
            value={draft.currency}
            onChange={(e) => set('currency', e.target.value as SupportedMoneyDto['currency'])}
            options={SupportedMoneyDto.shape.currency.options.map((c) => ({ value: c, label: c }))}
          />
        </Field>
      </div>
      <div>
        <Button
          variant="secondary"
          onClick={generate}
          disabled={combos === 0 || combos > VARIANT_MATRIX_MAX}
          data-testid="generate-matrix"
        >
          Generate {combos} SKU{combos === 1 ? '' : 's'}
        </Button>
      </div>
      {draft.variants.length ? (
        <table className="wdc-table" data-testid="variant-table">
          <caption className="wd-muted">
            Stock per SKU — units are created when a prize is built from this product.
          </caption>
          <thead>
            <tr>
              <th>SKU</th>
              {draft.optionAxes
                .filter((a) => a.values.length)
                .map((a) => (
                  <th key={a.axisId}>{a.name.en ?? a.axisId}</th>
                ))}
              <th>Value (minor)</th>
              <th>Stock</th>
              <th>Images</th>
            </tr>
          </thead>
          <tbody>
            {draft.variants.map((v, vi) => (
              <tr key={v.sku}>
                <td>
                  <code>{v.sku}</code>
                </td>
                {draft.optionAxes
                  .filter((a) => a.values.length)
                  .map((a) => (
                    <td key={a.axisId}>
                      {a.values.find((x) => x.valueId === v.options[a.axisId])?.label.en ??
                        v.options[a.axisId]}
                    </td>
                  ))}
                <td>
                  <Input
                    aria-label={`Value of ${v.sku}`}
                    inputMode="numeric"
                    value={v.value.amountMinor}
                    onChange={(e) =>
                      set(
                        'variants',
                        draft.variants.map((x, i) =>
                          i === vi
                            ? {
                                ...x,
                                value: {
                                  ...x.value,
                                  amountMinor: e.target.value.replace(/[^0-9]/g, '') || '0',
                                },
                              }
                            : x,
                        ),
                      )
                    }
                  />
                </td>
                <td>
                  <Input
                    aria-label={`Stock of ${v.sku}`}
                    type="number"
                    min={0}
                    value={v.stock}
                    onChange={(e) =>
                      set(
                        'variants',
                        draft.variants.map((x, i) =>
                          i === vi ? { ...x, stock: Math.max(0, Number(e.target.value) || 0) } : x,
                        ),
                      )
                    }
                  />
                </td>
                <td data-testid={`images-${v.sku}`}>{v.imageRefs?.length ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      <h2>Images</h2>
      <AssetUploader
        purpose="PRIZE_IMAGE"
        label="Hero image"
        altSuggestion={draft.name ? `${draft.name} hero shot` : ''}
        onUploaded={(a) => set('heroImageRef', a.ref)}
      />
      {draft.heroImageRef ? (
        <p className="wd-muted">
          Hero: <code>{draft.heroImageRef}</code>
        </p>
      ) : null}
      <h3>Images per colour</h3>
      <SwatchBulkUpload
        productName={draft.name || 'Product'}
        colours={(colourAxis?.values ?? []).map((v) => ({
          valueId: v.valueId,
          label: v.label.en ?? v.valueId,
          swatch: v.swatch,
        }))}
        onAssigned={(colour, refs) =>
          colourAxis && set('variants', assignSwatchImages(draft.variants, colourAxis.axisId, colour, refs))
        }
      />
      <Checkbox
        label="Also use the first colour's images as the product gallery"
        checked={false}
        onCheckedChange={(c) => {
          if (c && colourAxis?.values[0])
            set(
              'galleryRefs',
              draft.variants.find((v) => v.options[colourAxis.axisId] === colourAxis.values[0]!.valueId)
                ?.imageRefs ?? [],
            );
        }}
      />
    </div>
  );
}
